import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Calendar, Plus, Trash2, Sparkles, Clock, CheckCircle2, RefreshCw, Loader2 } from 'lucide-react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { API } from '../App';

const ContentCalendar = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [calendar, setCalendar] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showBlogModal, setShowBlogModal] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [regeneratingImage, setRegeneratingImage] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchCalendar();
  }, [user, navigate]);

  const fetchCalendar = async () => {
    try {
      const response = await axios.get(`${API}/calendar/${user.id}`);
      
      // Backend now returns { calendar_items, social_posts }
      const { calendar_items = [], social_posts = [] } = response.data;
      
      // Combine and organize by date
      const organized = {};
      
      // Add calendar items (blogs)
      calendar_items.forEach(item => {
        const date = item.scheduled_date;
        if (!organized[date]) {
          organized[date] = { blogs: [], social_posts: [] };
        }
        organized[date].blogs.push(item);
      });
      
      // Add social posts
      social_posts.forEach(post => {
        const date = post.scheduled_date;
        if (!organized[date]) {
          organized[date] = { blogs: [], social_posts: [] };
        }
        organized[date].social_posts.push(post);
      });
      
      setCalendar(organized);
    } catch (error) {
      console.error('Error fetching calendar:', error);
    }
  };

  // Generate calendar days for current month
  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty slots for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add actual days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateStr = date.toISOString().split('T')[0];
      const dayData = calendar[dateStr] || { blogs: [], social_posts: [] };
      days.push({ date, dateStr, ...dayData });
    }
    
    return days;
  };

  const handleDateClick = (day) => {
    if (!day) return;
    setSelectedDate(day);
    setShowScheduleModal(true);
  };

  const handleScheduleQuestions = async () => {
    if (!selectedQuestions.length || !selectedDate) return;
    
    setLoading(true);
    try {
      const items = selectedQuestions.map(q => ({
        question: q,
        scheduled_date: selectedDate.dateStr
      }));
      
      await axios.post(`${API}/calendar/schedule?user_id=${user.id}`, items);
      await fetchCalendar();
      setShowScheduleModal(false);
      setSelectedQuestions([]);
      setAvailableQuestions([]);
    } catch (error) {
      console.error('Error scheduling:', error);
      alert('Error scheduling content');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await axios.delete(`${API}/calendar/${itemId}`);
      await fetchCalendar();
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  const handleViewBlog = async (blogId) => {
    try {
      const response = await axios.get(`${API}/library/blog/${blogId}`);
      setSelectedBlog(response.data);
      setShowBlogModal(true);
    } catch (error) {
      console.error('Error fetching blog:', error);
      alert('Error loading blog content');
    }
  };

  const handleCopyBlog = () => {
    if (!selectedBlog) return;
    const text = `${selectedBlog.title}\n\n${selectedBlog.content}`;
    
    // Use textarea fallback method (more reliable)
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    
    try {
      document.execCommand('copy');
      alert('Blog copied to clipboard!');
    } catch (err) {
      console.error('Copy failed:', err);
      alert('Could not copy. Please select and copy manually.');
    } finally {
      document.body.removeChild(textarea);
    }
  };

  const handleGenerateScheduled = async () => {
    // Get all pending blogs from calendar
    const pending = Object.values(calendar).flatMap(day => 
      day.blogs.filter(blog => blog.status === 'pending')
    );
    
    if (!pending.length) {
      alert('No pending content to generate!');
      return;
    }

    setLoading(true);
    try {
      const questions = pending.map(blog => blog.question);
      await axios.post(`${API}/generate/bulk`, {
        user_id: user.id,
        questions: questions
      });
      
      alert(`Generating ${questions.length} blogs! Check your Content Library in a few minutes.`);
      await fetchCalendar();
    } catch (error) {
      console.error('Error generating:', error);
      alert('Error generating content');
    } finally {
      setLoading(false);
    }
  };

  const days = getDaysInMonth();
  const monthName = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'generating': return 'bg-blue-100 text-blue-800';
      case 'generated': return 'bg-green-100 text-green-800';
      case 'published': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center space-x-2 md:space-x-4 min-w-0">
              <Button onClick={() => navigate('/dashboard')} variant="ghost" size="sm" className="flex-shrink-0">
                <ArrowLeft className="w-4 h-4 md:w-5 md:h-5" />
              </Button>
              <div className="flex items-center space-x-2 min-w-0">
                <Calendar className="w-5 h-5 md:w-6 md:h-6 text-orange-600 flex-shrink-0" />
                <span className="text-base md:text-xl font-bold text-gray-900 truncate">Content Calendar</span>
              </div>
            </div>
            <div className="flex items-center space-x-2 md:space-x-3 flex-shrink-0">
              <Button 
                onClick={() => navigate('/content-library')}
                variant="outline"
                size="sm"
                className="hidden sm:flex"
              >
                View Library
              </Button>
              <Button 
                onClick={handleGenerateScheduled}
                disabled={loading || Object.values(calendar).flatMap(day => day.blogs.filter(b => b.status === 'pending')).length === 0}
                className="bg-orange-600 hover:bg-orange-700 text-white text-xs md:text-sm px-2 md:px-4 py-2"
                size="sm"
              >
                <Sparkles className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                <span className="hidden sm:inline">Generate All</span>
                <span className="sm:hidden">Gen</span>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8">
          <div className="bg-white rounded-xl p-4 md:p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs md:text-sm text-gray-600">Blogs Scheduled</span>
              <Calendar className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
            </div>
            <p className="text-2xl md:text-3xl font-bold text-gray-900">
              {Object.values(calendar).reduce((sum, day) => sum + day.blogs.length, 0)}
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 md:p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs md:text-sm text-gray-600">Social Posts</span>
              <Sparkles className="w-4 h-4 md:w-5 md:h-5 text-purple-600" />
            </div>
            <p className="text-2xl md:text-3xl font-bold text-gray-900">
              {Object.values(calendar).reduce((sum, day) => sum + day.social_posts.length, 0)}
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 md:p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs md:text-sm text-gray-600">Total Items</span>
              <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
            </div>
            <p className="text-2xl md:text-3xl font-bold text-gray-900">
              {Object.values(calendar).reduce((sum, day) => sum + day.blogs.length + day.social_posts.length, 0)}
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 md:p-6 border border-orange-200 bg-orange-50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs md:text-sm text-orange-700 font-semibold">Monthly Goal</span>
              <Sparkles className="w-4 h-4 md:w-5 md:h-5 text-orange-600" />
            </div>
            <p className="text-2xl md:text-3xl font-bold text-orange-600">
              {Object.values(calendar).reduce((sum, day) => sum + day.blogs.length + day.social_posts.length, 0)}/30
            </p>
          </div>
        </div>

        {/* Calendar Header */}
        <div className="bg-white rounded-2xl p-4 md:p-8 shadow-lg border border-gray-200 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900">{monthName}</h2>
            <div className="flex items-center space-x-2 md:space-x-3">
              <Button
                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                variant="outline"
                size="sm"
                className="text-xs md:text-sm"
              >
                ← <span className="hidden sm:inline">Previous</span>
              </Button>
              <Button
                onClick={() => setCurrentMonth(new Date())}
                variant="outline"
                size="sm"
                className="text-xs md:text-sm"
              >
                Today
              </Button>
              <Button
                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                variant="outline"
                size="sm"
                className="text-xs md:text-sm"
              >
                <span className="hidden sm:inline">Next</span> →
              </Button>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1 md:gap-4">
            {/* Day headers */}
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center font-semibold text-gray-600 text-xs md:text-sm py-2">
                <span className="hidden sm:inline">{day}</span>
                <span className="sm:hidden">{day[0]}</span>
              </div>
            ))}
            
            {/* Calendar days */}
            {days.map((day, index) => (
              <div
                key={index}
                onClick={() => handleDateClick(day)}
                className={`
                  min-h-16 md:min-h-24 p-1 md:p-2 rounded-lg border-2 transition-all
                  ${!day ? 'bg-gray-50 border-transparent' : 'bg-white border-gray-200 hover:border-orange-400 cursor-pointer hover:shadow-md'}
                `}
              >
                {day && (
                  <>
                    <div className="text-xs md:text-sm font-semibold text-gray-900 mb-1">
                      {day.date.getDate()}
                    </div>
                    
                    {/* Render blogs */}
                    {day.blogs && day.blogs.map((blog, i) => (
                      <div key={`blog-${i}`} className={`text-[10px] md:text-xs px-1 md:px-2 py-0.5 md:py-1 rounded mb-1 truncate ${getStatusColor(blog.status)}`}>
                        <span className="hidden sm:inline">
                          📝 Blog
                        </span>
                        <span className="sm:hidden">📝</span>
                      </div>
                    ))}
                    
                    {/* Render social posts */}
                    {day.social_posts && day.social_posts.map((post, i) => (
                      <div key={`social-${i}`} className="text-[10px] md:text-xs px-1 md:px-2 py-0.5 md:py-1 rounded mb-1 truncate bg-purple-100 text-purple-800">
                        <span className="hidden sm:inline">
                          📱 Social
                        </span>
                        <span className="sm:hidden">📱</span>
                      </div>
                    ))}
                    
                    {(!day.blogs || day.blogs.length === 0) && (!day.social_posts || day.social_posts.length === 0) && (
                      <div className="text-xs text-gray-400 mt-2 hidden sm:block">+ Add</div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Quick Add from Keyword Research */}
        <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-8 border-2 border-orange-200">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">🚀 Quick Tip</h3>
          <p className="text-gray-700 mb-4">
            Go to <strong>Keyword Research</strong>, find questions your customers ask, then come back here to schedule them across the month.
          </p>
          <Button 
            onClick={() => navigate('/keyword-research')}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            Go to Keyword Research →
          </Button>
        </div>
      </div>

      {/* Schedule Modal */}
      {showScheduleModal && selectedDate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <h3 className="text-2xl font-bold mb-4">
              Schedule Content for {selectedDate.date.toLocaleDateString()}
            </h3>
            
            {/* Blogs Section */}
            {selectedDate.blogs && selectedDate.blogs.length > 0 && (
              <div className="mb-6">
                <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  📝 Scheduled Blogs ({selectedDate.blogs.length})
                </p>
                {selectedDate.blogs.map((blog, i) => (
                  <div key={i} className="bg-blue-50 rounded-lg p-4 mb-2 border border-blue-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{blog.question}</p>
                        <span className={`text-xs px-2 py-1 rounded mt-2 inline-block ${getStatusColor(blog.status)}`}>
                          {blog.status}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        {blog.status === 'generated' && blog.generated_blog_id && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewBlog(blog.generated_blog_id)}
                            className="text-orange-600 border-orange-300 hover:bg-orange-50"
                          >
                            View Blog
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteItem(blog.id)}
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Social Posts Section */}
            {selectedDate.social_posts && selectedDate.social_posts.length > 0 && (
              <div className="mb-6">
                <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  📱 Scheduled Social Posts ({selectedDate.social_posts.length})
                </p>
                {selectedDate.social_posts.map((post, i) => (
                  <div key={i} className="bg-purple-50 rounded-lg p-4 mb-2 border border-purple-200">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-semibold text-purple-700 uppercase">{post.post_type}</span>
                          <span className="text-xs text-gray-500">•</span>
                          <span className="text-xs text-gray-600">{post.character_count} chars</span>
                        </div>
                        <p className="text-sm text-gray-900 line-clamp-2 mb-2">{post.post_text}</p>
                        <div className="flex flex-wrap gap-1 mb-2">
                          {post.platforms && post.platforms.map((platform, idx) => (
                            <span key={idx} className="text-xs px-2 py-0.5 bg-purple-200 text-purple-800 rounded">
                              {platform}
                            </span>
                          ))}
                        </div>
                        {post.hashtags && (
                          <p className="text-xs text-purple-600">{post.hashtags}</p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate('/content-library')}
                        className="text-purple-600 border-purple-300 hover:bg-purple-50 flex-shrink-0"
                      >
                        View in Library
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {(!selectedDate.blogs || selectedDate.blogs.length === 0) && 
             (!selectedDate.social_posts || selectedDate.social_posts.length === 0) && (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No content scheduled for this date</p>
                <p className="text-sm mt-2">Go to Content Library to schedule content</p>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowScheduleModal(false)}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Blog View Modal */}
      {showBlogModal && selectedBlog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-3xl font-bold text-gray-900">{selectedBlog.title}</h2>
              <Button
                variant="ghost"
                onClick={() => {
                  setShowBlogModal(false);
                  setSelectedBlog(null);
                }}
              >
                ✕
              </Button>
            </div>

            {/* Featured Image Section */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-gray-700">Featured Image</h3>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    try {
                      setRegeneratingImage(true);
                      const response = await fetch(`${API}/generate/blog-image`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          blog_id: selectedBlog.id,
                          title: selectedBlog.title,
                          industry: user?.industry || 'business'
                        })
                      });
                      const data = await response.json();
                      if (data.success) {
                        setSelectedBlog({ ...selectedBlog, featured_image_url: data.image_url });
                      }
                    } catch (error) {
                      console.error('Error regenerating image:', error);
                    } finally {
                      setRegeneratingImage(false);
                    }
                  }}
                  disabled={regeneratingImage}
                  className="text-orange-600 border-orange-300 hover:bg-orange-50"
                >
                  {regeneratingImage ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Regenerate Image
                    </>
                  )}
                </Button>
              </div>
              {selectedBlog.featured_image_url ? (
                <img 
                  src={selectedBlog.featured_image_url} 
                  alt={selectedBlog.title}
                  className="w-full h-64 object-cover rounded-lg border border-gray-200"
                />
              ) : (
                <div className="w-full h-64 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400">
                  {regeneratingImage ? 'Generating image...' : 'No image generated yet'}
                </div>
              )}
            </div>

            <div className="mb-6 flex flex-wrap gap-3">
              <div className="text-sm text-gray-600">
                <strong>Words:</strong> {selectedBlog.word_count || 'N/A'}
              </div>
              <div className="text-sm text-gray-600">
                <strong>Question:</strong> {selectedBlog.question}
              </div>
            </div>

            <div className="prose prose-lg max-w-none mb-6 prose-headings:text-gray-900 prose-headings:font-bold prose-h2:text-2xl prose-h2:mt-6 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-4 prose-h3:mb-3 prose-p:text-gray-700 prose-p:leading-relaxed prose-ul:text-gray-700 prose-ol:text-gray-700 prose-li:my-1 prose-strong:text-gray-900">
              <ReactMarkdown>{selectedBlog.content}</ReactMarkdown>
            </div>

            <div className="border-t pt-6 mt-6">
              <h3 className="font-bold text-lg mb-3">SEO Metadata</h3>
              <div className="space-y-2 text-sm">
                <div><strong>Meta Title:</strong> {selectedBlog.meta_title}</div>
                <div><strong>Meta Description:</strong> {selectedBlog.meta_description}</div>
                <div><strong>Slug:</strong> {selectedBlog.slug}</div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                onClick={handleCopyBlog}
                className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
              >
                Copy All Text
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowBlogModal(false);
                  setSelectedBlog(null);
                }}
                className="flex-1"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentCalendar;
